import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { notNull } from '../../shared';
import { Router } from '@angular/router';
import { CacheService } from './cache-service';
import { AuthenticatedUser } from '../models/user/authenticated-user';
import { SharedRoutes } from '../routes/shared-routes';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private router = inject(Router);
  private cacheService = inject(CacheService);

  private _authenticatedUser = new BehaviorSubject<AuthenticatedUser | null>(null);
  public authenticatedUser$ = this._authenticatedUser.asObservable();

  private _refreshingSession = new BehaviorSubject<boolean>(false);
  public refreshingSession$ = this._refreshingSession.asObservable();

  public destroySession: Subject<boolean> = new Subject<boolean>();
  private userCacheKey = 'CachedUser';

  constructor() {
    this.setupBindings();
  }

  public setupBindings() {
    this._authenticatedUser.subscribe(user => {
      if (user) {
        // save user to persistent cache
        this.cacheService.storeData(this.userCacheKey, user);
      }
    });

    this.destroySession.pipe(notNull()).subscribe((shouldDestroy: boolean) => {
      if (shouldDestroy) {
        this.clearUserCacheData();
        this._authenticatedUser.next(null);
        this.router.navigate([SharedRoutes.auth()]).then();
      }
    });
  }

  public setRefreshingSession(refreshingSession: boolean) {
    this._refreshingSession.next(refreshingSession);
  }

  public setAuthenticatedUser(user: AuthenticatedUser) {
    this._authenticatedUser.next(user);
  }

  public logout() {
    this.destroySession.next(true);
    this.router.navigate([SharedRoutes.auth()]).then();
  }

  public getCachedAuthenticatedUser(): AuthenticatedUser | null {
    return this.cacheService.loadData(this.userCacheKey);
  }

  private clearUserCacheData() {
    this.cacheService.removeData(this.userCacheKey);
  }
}
